import React from "react";
import { Link } from "react-router-dom";
import "../C2Invitations.css";
import PortalHeader from "../PortalHeader";

import InvitationsList from "./InvitationsList";
import WizardStepOne from "./WizardStepOne";
import WizardStepThree from "./WizardStepThree";
import WizardStepTwo from "./WizardStepTwo";
import { CopyIcon, EyeIcon, EyeOffIcon } from "./pwIcons";
import { useInvitationsState } from "./useInvitationsState";

const C2Invitations: React.FC = () => {
  const state = useInvitationsState();
  const {
    showInviteForm,
    inviteMessage,
    registrationResponse,
    newInvite,
    formAssessment,
    assessedClients,
    loadingAssessments,
    wizardStep,
    handleLogout,
    handleOpenInviteForm,
    handleCloseForm,
    handleRegisterAnother,
    handleOrgNameChange,
    handleInviteSend,
    handleCopyLink,
    setInviteMessage,
    setWizardStep,
    RISK_BADGE_CONFIG,
    formatDateShort,
  } = state;

  // Temporary password stays masked until the user explicitly reveals it.
  const [showPassword, setShowPassword] = React.useState(false);

  // Reset to masked whenever a new account is created.
  React.useEffect(() => {
    setShowPassword(false);
  }, [registrationResponse?.loginId]);

  const pwActionBtnStyle: React.CSSProperties = {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    padding: "4px 10px",
    fontSize: 12,
    fontWeight: 600,
    color: "#475569",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    lineHeight: 1.2,
  };

  return (
    <div className="invitations-container" data-testid="invitations-container">
      <PortalHeader onLogout={handleLogout} />

      <div className="invitations-content">
        <div className="welcome-section">
          <div className="relative pl-5">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-violet-500 to-purple-600 rounded-full" />
            <h1
              data-testid="invitations-title"
              style={{
                background:
                  "linear-gradient(135deg, #6d28d9 0%, #7c3aed 50%, #a855f7 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                margin: "0 0 4px 0",
              }}
            >
              Invitations
            </h1>
            <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>
              Manage user invitations and team members
            </p>
          </div>
          {!showInviteForm && (
            <button
              className="btn-invite"
              onClick={handleOpenInviteForm}
              data-testid="new-invite-btn"
            >
              + Create Additional Invitation
            </button>
          )}
        </div>

        {inviteMessage && (
          <div
            className={`invite-message ${inviteMessage.includes("✅") ? "success" : "error"}`}
            data-testid="invite-message"
          >
            {inviteMessage}
          </div>
        )}

        {showInviteForm && (
          <div className="invite-form-card" id="invite-form">
            <h2>Register Your Customer</h2>
            <p className="form-description">
              📋 Register your customer so that they can initiate the compliance checks.
            </p>

            {/* ── Registration Success ── */}
            {registrationResponse?.action === "SUCCESS" ? (
              <div style={{ padding: "24px 0" }}>
                {/* Success header */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>✓</div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#111" }}>Account Created</h3>
                    <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>Login credentials for {newInvite.organisationName}</p>
                  </div>
                </div>

                {/* Credentials card */}
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 24px", marginBottom: 20 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 32px" }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Username</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#1e293b", fontFamily: "monospace" }}>{registrationResponse.loginId}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Temporary Password</div>
                      {registrationResponse.tempPassword ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                          <div
                            style={{
                              fontSize: 16,
                              fontWeight: 700,
                              color: "#1e293b",
                              fontFamily: "monospace",
                              letterSpacing: showPassword ? "normal" : "3px",
                              userSelect: showPassword ? "text" : "none",
                            }}
                            data-testid="temp-password-value"
                          >
                            {showPassword ? registrationResponse.tempPassword : "••••••••••"}
                          </div>
                          {!showPassword ? (
                            <button
                              type="button"
                              style={pwActionBtnStyle}
                              onClick={() => setShowPassword(true)}
                              data-testid="reveal-password-btn"
                            >
                              <EyeIcon /> Show
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                style={pwActionBtnStyle}
                                onClick={() => {
                                  navigator.clipboard.writeText(registrationResponse.tempPassword ?? "");
                                  setInviteMessage("✅ Password copied");
                                  setTimeout(() => setInviteMessage(""), 3000);
                                }}
                                data-testid="copy-password-btn"
                              >
                                <CopyIcon /> Copy
                              </button>
                              <button
                                type="button"
                                style={pwActionBtnStyle}
                                onClick={() => setShowPassword(false)}
                                data-testid="hide-password-btn"
                              >
                                <EyeOffIcon /> Hide
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: 16, fontWeight: 700, color: "#1e293b", fontFamily: "monospace" }}>—</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Copy button */}
                <button
                  type="button"
                  style={{
                    width: "100%",
                    padding: "14px 20px",
                    background: "linear-gradient(135deg, #1B3A6B 0%, #2563EB 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 10,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    boxShadow: "0 2px 8px rgba(27,58,107,0.2)",
                    marginBottom: 8,
                  }}
                  onClick={() => {
                    const text = [
                      `Hi,`,
                      ``,
                      `Your ARCaml CDD verification account has been created.`,
                      ``,
                      `Login URL: https://verify.arcaml.com.au`,
                      `Username: ${registrationResponse.loginId}`,
                      registrationResponse.tempPassword ? `Temporary Password: ${registrationResponse.tempPassword}` : null,
                      ``,
                      `Please log in and change your password on first login.`,
                      ``,
                      `Regards`,
                    ].filter(Boolean).join("\n");
                    navigator.clipboard.writeText(text);
                    setInviteMessage("✅ Credentials copied — paste into an email to your customer");
                    setTimeout(() => setInviteMessage(""), 3000);
                  }}
                >
                  📋 Copy Credentials
                </button>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    background: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    borderLeft: "3px solid #2563EB",
                    borderRadius: 8,
                    padding: "14px 16px",
                    margin: "14px 0 20px",
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#2563EB"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ flexShrink: 0, marginTop: 1 }}
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", lineHeight: 1.4, margin: "0 0 2px" }}>
                      Copy these credentials before you continue
                    </p>
                    <p style={{ fontSize: 13, fontWeight: 500, color: "#475569", lineHeight: 1.5, margin: 0 }}>
                      Send them to the contact for{" "}
                      <strong style={{ color: "#334155" }}>{newInvite.organisationName}</strong> so they can log in.
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 12, borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={handleRegisterAnother}
                    style={{ flex: 1 }}
                  >
                    + Register Another
                  </button>
                  <button
                    type="button"
                    className="btn-done"
                    data-testid="invite-done-btn"
                    onClick={handleCloseForm}
                    style={{ flex: 1 }}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : newInvite.generatedLink ? (
              /* ── Generated Link ── */
              <div className="generated-link-section">
                <h3>✅ Link Generated</h3>
                <p className="link-instruction">
                  Copy this link and send it to your customer:
                </p>
                <div className="link-display">
                  <code>{newInvite.generatedLink}</code>
                  <button
                    type="button"
                    className="btn-copy-link"
                    data-testid="copy-invite-link-btn"
                    onClick={handleCopyLink}
                  >
                    📋 Copy Link
                  </button>
                </div>
                <p className="link-validity">
                  This link will expire in 30 days (
                  {formatDateShort(
                    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                  )}
                  )
                </p>
                <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={handleRegisterAnother}
                  >
                    + Register Another Contact
                  </button>
                  <button
                    type="button"
                    className="btn-done"
                    data-testid="invite-done-btn"
                    onClick={handleCloseForm}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              /* ── Wizard Form ── */
              <form onSubmit={handleInviteSend}>
                {assessedClients.length === 0 ? (
                  <div
                    className="assessment-status-panel not-assessed"
                    style={{ marginBottom: 20 }}
                  >
                    <span className="assessment-status-icon">
                      {loadingAssessments ? "⏳" : "🚫"}
                    </span>
                    <div className="assessment-status-text">
                      <strong>
                        {loadingAssessments
                          ? "Loading assessments…"
                          : "No risk assessments found"}
                      </strong>
                      {!loadingAssessments && (
                        <span>
                          {" "}
                          — You must complete a Risk Assessment before you can
                          send an invite.
                        </span>
                      )}
                    </div>
                    {!loadingAssessments && (
                      <Link
                        to="/portal/risk-assessment"
                        className="assessment-status-link"
                      >
                        Go to Risk Assessment →
                      </Link>
                    )}
                  </div>
                ) : (
                  <>
                    {/* ── C2 Entity Selector ── */}
                    <div className="form-row">
                      <div className="form-group" style={{ flex: 1 }}>
                        <label>Customer Entity (Assessed) *</label>
                        <select
                          value={formAssessment?.id ?? ""}
                          onChange={(e) => handleOrgNameChange(e.target.value)}
                          required
                          className="filter-select"
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            fontSize: 14,
                          }}
                        >
                          <option value="">
                            — Select an assessed entity —
                          </option>
                          {assessedClients.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.clientName || "(No name)"} ·{" "}
                              {a.riskRating === "HIGH"
                                ? "🔴"
                                : a.riskRating === "MEDIUM"
                                  ? "🟡"
                                  : "🟢"}{" "}
                              {a.riskRating}
                              {" · "}
                              {a.savedAt
                                ? new Date(a.savedAt).toLocaleDateString("en-AU", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                      {newInvite.email && (
                        <div className="form-group" style={{ flex: 1 }}>
                          <label>Reference (from assessment)</label>
                          <input
                            type="text"
                            value={newInvite.email}
                            readOnly
                            className="input-readonly"
                            title="Auto-filled from saved risk assessment"
                          />
                        </div>
                      )}
                    </div>

                    {/* ── Risk Assessment Status ── */}
                    {newInvite.organisationName?.trim() && (
                      <div
                        className={`assessment-status-panel ${formAssessment ? "assessed" : "not-assessed"}`}
                      >
                        {formAssessment ? (
                          <>
                            <span className="assessment-status-icon">🛡️</span>
                            <div className="assessment-status-text">
                              <strong>Risk Assessment on file</strong>
                              <span>
                                {" — "}
                                <span
                                  className="risk-inline-badge"
                                  style={{
                                    background:
                                      RISK_BADGE_CONFIG[
                                        formAssessment.riskRating
                                      ].bg,
                                    color:
                                      RISK_BADGE_CONFIG[
                                        formAssessment.riskRating
                                      ].color,
                                  }}
                                >
                                  {formAssessment.riskRating === "HIGH"
                                    ? "🔴"
                                    : formAssessment.riskRating === "MEDIUM"
                                      ? "🟡"
                                      : "🟢"}{" "}
                                  {
                                    RISK_BADGE_CONFIG[formAssessment.riskRating]
                                      .label
                                  }
                                </span>{" "}
                                · Assessed{" "}
                                {formatDateShort(formAssessment.savedAt)}
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            <span className="assessment-status-icon">⚠️</span>
                            <div className="assessment-status-text">
                              <strong>No risk assessment found</strong>
                              <span>
                                {" "}
                                — Please assess this customer before sending an
                                invite.
                              </span>
                            </div>
                            <Link
                              to={`/portal/risk-assessment?client=${encodeURIComponent(newInvite.organisationName)}`}
                              className="assessment-status-link"
                            >
                              Assess Now →
                            </Link>
                          </>
                        )}
                      </div>
                    )}

                    {/* ── Registration Wizard ── */}
                    {formAssessment && (
                      <div
                        className="registration-fields-section"
                        style={{ marginTop: 20 }}
                      >
                        {/* Progress Bar */}
                        <div style={{ marginBottom: 24 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0,
                              marginBottom: 8,
                            }}
                          >
                            {[
                              "Entity Details",
                              "Contact Details",
                              "Review",
                            ].map((label, idx) => {
                              const step = idx + 1;
                              const isActive = wizardStep === step;
                              const isDone = wizardStep > step;
                              return (
                                <React.Fragment key={step}>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      flex: 1,
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: "50%",
                                        background: isDone
                                          ? "#00a699"
                                          : isActive
                                            ? "#2563eb"
                                            : "#e5e7eb",
                                        color:
                                          isDone || isActive
                                            ? "#fff"
                                            : "#9ca3af",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: 13,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {isDone ? "✓" : step}
                                    </div>
                                    <span
                                      style={{
                                        fontSize: 11,
                                        marginTop: 4,
                                        color: isActive
                                          ? "#2563eb"
                                          : isDone
                                            ? "#00a699"
                                            : "#9ca3af",
                                        fontWeight: isActive ? 600 : 400,
                                      }}
                                    >
                                      {label}
                                    </span>
                                  </div>
                                  {idx < 2 && (
                                    <div
                                      style={{
                                        flex: 2,
                                        height: 2,
                                        background:
                                          wizardStep > step
                                            ? "#00a699"
                                            : "#e5e7eb",
                                        marginBottom: 20,
                                      }}
                                    />
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </div>

                        {/* Step content */}
                        {wizardStep === 1 && <WizardStepOne state={state} />}
                        {wizardStep === 2 && <WizardStepTwo state={state} />}
                        {wizardStep === 3 && <WizardStepThree state={state} />}
                      </div>
                    )}
                  </>
                )}
              </form>
            )}
          </div>
        )}

        {/* Invitation list / table — hidden while create form is open */}
        {!showInviteForm && <InvitationsList state={state} />}
      </div>
    </div>
  );
};

export default C2Invitations;
